import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@Components/Button';
import { Design, Size } from '@Components/designConstants';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import {
  ApiSentryRun,
  ApiSentrySource,
  ApiSentrySourceInput,
  ApiSentryUrlEntry
} from '@blisspointmedia/bpm-types/dist/ApiSentry';
import {
  createAdminSource,
  deleteAdminSource,
  getRunStatus,
  listAdminSources,
  setAdminSourceEnabled,
  startRun,
  updateAdminSource
} from '../api-sentry-api';

const SOURCES_QUERY_KEY = ['api-sentry-admin-sources'];

const DEFAULT_URLS_JSON = '[\n  { "url": "https://...", "type": "release_notes" }\n]';

interface SourceFormProps {
  initialPlatformName?: string;
  initialUrls?: ApiSentryUrlEntry[];
  submitLabel: string;
  onSubmit: (input: ApiSentrySourceInput) => Promise<void>;
  onCancel: () => void;
}

const SourceForm = ({
  initialPlatformName = '',
  initialUrls,
  submitLabel,
  onSubmit,
  onCancel
}: SourceFormProps) => {
  const [platformName, setPlatformName] = useState(initialPlatformName);
  const [urlsJson, setUrlsJson] = useState(
    initialUrls ? JSON.stringify(initialUrls, null, 2) : DEFAULT_URLS_JSON
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setError(null);
    let urls;
    try {
      urls = JSON.parse(urlsJson);
    } catch {
      setError('URLs must be valid JSON — an array of { url, type } entries.');
      return;
    }
    if (!platformName.trim()) {
      setError('Platform name is required.');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({ platformName: platformName.trim(), urls });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='api-sentry-source-form'>
      <input
        type='text'
        placeholder='Platform name (e.g. Google Ads)'
        value={platformName}
        onChange={e => setPlatformName(e.target.value)}
      />
      <textarea
        rows={6}
        value={urlsJson}
        onChange={e => setUrlsJson(e.target.value)}
        placeholder='URLs config as JSON array'
      />
      {error && <div className='api-sentry-form-error'>{error}</div>}
      <div className='api-sentry-form-actions'>
        <Button size={Size.SM} onClick={submit} loading={saving}>
          {submitLabel}
        </Button>
        <Button design={Design.NEUTRAL} size={Size.SM} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const NewSourceForm = ({ onCreated }: { onCreated: () => void }) => {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button size={Size.SM} onClick={() => setOpen(true)}>
        + New source
      </Button>
    );
  }

  return (
    <SourceForm
      submitLabel='Create'
      onSubmit={async input => {
        await createAdminSource(input);
        setOpen(false);
        onCreated();
      }}
      onCancel={() => setOpen(false)}
    />
  );
};

const RunPanel = () => {
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<ApiSentryRun | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  const handleStart = async () => {
    setStarting(true);
    try {
      const { runId: newRunId } = await startRun();
      setRunId(newRunId);
      stopPolling();
      pollRef.current = setInterval(async () => {
        const status = await getRunStatus(newRunId);
        setRun(status);
        if (status.status === 'done' || status.status === 'failed') {
          stopPolling();
        }
      }, 2000);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className='api-sentry-run-panel'>
      <Button size={Size.SM} onClick={handleStart} disabled={starting || run?.status === 'running'}>
        {run?.status === 'running' ? 'Running...' : 'Run now'}
      </Button>
      {run && (
        <div className='api-sentry-run-status'>
          <div className='api-sentry-run-progress-bar'>
            <div className='api-sentry-run-progress-fill' style={{ width: `${run.progress}%` }} />
          </div>
          <div className='api-sentry-run-meta'>
            Run {runId} — {run.status} ({run.progress}%)
          </div>
          {run.error && <div className='api-sentry-form-error'>{run.error}</div>}
          <div className='api-sentry-run-log'>
            {run.log.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface SourceRowProps {
  source: ApiSentrySource;
  onToggle: (id: string, enabled: boolean) => void;
  onDelete: (id: string) => void;
  onUpdated: () => void;
}

const SourceRow = ({ source, onToggle, onDelete, onUpdated }: SourceRowProps) => {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className='api-sentry-source-row api-sentry-source-row--editing'>
        <SourceForm
          initialPlatformName={source.platformName}
          initialUrls={source.urls}
          submitLabel='Save'
          onSubmit={async input => {
            await updateAdminSource(source.id, input);
            setEditing(false);
            onUpdated();
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className='api-sentry-source-row'>
      <div className='api-sentry-source-info'>
        <span className='api-sentry-source-name'>{source.platformName}</span>
        <span className='api-sentry-source-url-count'>{source.urls.length} URL(s)</span>
      </div>
      <div className='api-sentry-source-actions'>
        <ToggleSwitch
          label={source.enabled ? 'Enabled' : 'Disabled'}
          checked={source.enabled}
          onChange={() => onToggle(source.id, !source.enabled)}
        />
        <Button design={Design.NEUTRAL} size={Size.SM} onClick={() => setEditing(true)}>
          View/Edit
        </Button>
        <Button design={Design.NEUTRAL} size={Size.SM} onClick={() => onDelete(source.id)}>
          Delete
        </Button>
      </div>
    </div>
  );
};

export const AdminDashboard = () => {
  const queryClient = useQueryClient();
  const { data: sources = [] } = useQuery({
    queryKey: SOURCES_QUERY_KEY,
    queryFn: () => listAdminSources()
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: SOURCES_QUERY_KEY });

  const toggle = async (id: string, enabled: boolean) => {
    await setAdminSourceEnabled(id, enabled);
    invalidate();
  };

  const remove = async (id: string) => {
    await deleteAdminSource(id);
    invalidate();
  };

  return (
    <div className='api-sentry-admin-dashboard'>
      <div className='api-sentry-admin-dashboard-header'>
        <div className='api-sentry-section-title'>{sources.length} source(s) configured</div>
        <NewSourceForm onCreated={invalidate} />
      </div>

      <RunPanel />

      <div className='api-sentry-source-list'>
        {sources.map(source => (
          <SourceRow
            key={source.id}
            source={source}
            onToggle={toggle}
            onDelete={remove}
            onUpdated={invalidate}
          />
        ))}
      </div>
    </div>
  );
};
